/**
 * V70 엔글 마블 — 공통 타입
 *
 * Phase 2 FE + Phase 3 BE 공유 가능한 shape (서버 interface 는 mock/serverApi.ts).
 */
import type { EventKind } from '../config/eventConfig';

export type OfficialRankingStatus =
  | 'IN_PROGRESS'
  | 'AGGREGATING'
  | 'TIEBREAK_WAIT'
  | 'FINALIZED';

export interface BoardCell {
  index: number;
  kind: EventKind;
}

export interface MarbleSettings {
  enabled: boolean;
  diceDailyLimit: number;
  eventStartAt: number | null;
  eventEndAt: number | null;
  gracePeriodMinutes: number;
  leaderboardCacheTtlSec: number;
  leaderboardEpoch: number;
  officialRankingStatus: OfficialRankingStatus;
}

export interface MarbleBoard {
  epoch: number;
  cells: BoardCell[];
}

export interface InProgressEventState {
  eventId: string;
  kind: EventKind;
  /** 사용자 선택 가능 옵션 (서버 전송 shape 과 동일) */
  options?: Array<{
    id: string | number;
    label: string;
    /** UI 전시용 보상 분포 (사용자 교육 격차 완화 — 사용자 지시 §9.1) */
    rewards?: Array<{ value: number; prob: number; label?: string }>;
  }>;
  createdAt: number;
}

/**
 * 주사위 정책 (사용자 지시 §B).
 *   - limited: dailyLimit 회 / 일 (KST 자정 리셋)
 *   - unlimited: 횟수 제한 없음, 실제 roll 횟수는 통계용으로 누적
 */
export type DicePolicyMode = 'unlimited' | 'limited';
export interface DicePolicy {
  mode: DicePolicyMode;
  dailyLimit: number;
}
export const DEFAULT_DICE_POLICY: DicePolicy = { mode: 'limited', dailyLimit: 3 };

export interface DicePolicyChange {
  id: string;
  changedAt: number;
  changedByName: string | null;
  previous: DicePolicy;
  next: DicePolicy;
}

export interface DailyDiceStatus {
  mode: DicePolicyMode;
  used: number;
  /** limited 때만 유효. unlimited 는 null */
  limit: number | null;
  /** limited 때 remaining=max(0, limit-used). unlimited 는 null */
  remaining: number | null;
  diceDateYmd: string;
  /** V70G8D — 주말(KST 토·일): 신규 지급 없음, 금요일 잔여분 사용 */
  weekendCarryover?: boolean;
}

export interface MyState {
  uid: string;
  displayName: string;
  orgUnitId?: string;
  position: number;
  totalScore: number;
  /**
   * 하위 호환 필드. 서버가 반환할 때 limited 모드 기준 "남은 횟수"를 계산해 넣어줌.
   * **신뢰 기준은 아님** — 신뢰 기준은 `diceUsedToday`.
   */
  diceToday: number;
  /** V70 신규 — 당일 누적 roll 성공 횟수. 멱등으로 1회만 증가. 자정 리셋 */
  diceUsedToday: number;
  diceDateYmd: string;
  /** @deprecated V1 호환 — V2 부터 items.shields 사용. 마이그 시 items.shields 로 이관. */
  shields?: number;
  inProgressEvent: InProgressEventState | null;
  playCount: number;
  highScoreEvents: number;
  lossSum: number;
  epoch: number;
  updatedAt: number;
  /** V2 — 아이템 보유 (방패/가속) */
  items: { shields: number; speedBoosts: number };
  /** V2 — 무인도 상태 */
  isTrapped: boolean;
  /** V2 — 무인도 탈출 실패 누적 (0~2) */
  trapFails: number;
  /** V2 — 완주 바퀴 수 */
  lapsCompleted: number;
  /** V2 — 누적 완주 보상 (HUD 표시용) */
  lapsReward: number;
}

/** V2 Roll 요청 — useSpeedBoost 선택 가능 */
export interface RollDiceRequestV2 extends RollDiceRequest {
  useSpeedBoost?: boolean;
}

/** V2 Roll 응답 — 완주/trap/moveDistance 확장 */
export interface RollDiceResponseV2 extends RollDiceResponse {
  diceFace?: number;        // 실제 주사위 눈금 (무인도 판정용)
  moveDistance?: number;    // 실제 이동 거리 (가속 포함)
  lapsFromThisRoll?: number;
  trapOutcome?: 'none' | 'entered' | 'escaped' | 'rescued' | 'failed';
}

/** V2 상점 구매 요청 — Phase 3-C: 'pass' 추가 (BE 호환) */
export interface PurchaseItemRequest {
  clientRequestId: string;
  item: 'shield' | 'speedBoost' | 'pass';
}
export interface PurchaseItemResponse {
  success: boolean;
  item: 'shield' | 'speedBoost' | 'pass';
  priceDeducted: number;
  scoreAfter: number;
  itemsAfter: { shields: number; speedBoosts: number };
  isMock: boolean;
}

export interface LeaderboardRow {
  rank: number;
  uid: string;
  displayName: string;
  orgUnitId?: string;
  totalScore: number;
  playCount: number;
  highScoreEvents: number;
  lossSum: number;
}

export interface RollDiceRequest {
  clientRequestId: string;
}

export interface RollDiceResponse {
  rollId: string;
  dice: number;
  from: number;
  to: number;
  cellKind: EventKind;
  /** 즉시 처리 이벤트 결과 (E8/E10 등). delta 는 **이벤트 보상만** (바퀴 보상은 lapsReward 로 분리). */
  immediate?: {
    delta: number;
    scoreAfter: number;
    shieldConsumed?: boolean;
    label?: string;
    /** ISSUE-05: 방패 발동 시 "원래 손실 금액" (양의 정수). 결과창에 "-grossLoss → 방패 발동 → 0" 3단 표시용. */
    grossLoss?: number;
    /** QA Issue #4: E8 (방패 획득 칸) cap 초과 시 BE가 명시. FE가 "최대 보유 · 획득 실패" 안내 분기. */
    shieldsCapReached?: boolean;
  };
  /** 선택 이벤트 (E1/E3/E5/E6/E7 등) */
  pending?: InProgressEventState;
  /** 바퀴 완주 보상 (immediate/pending 모두 전적으로 분리 — 프론트 LapRewardOverlay 가 전담 표시) */
  lapsReward?: number;
  /** 세대 리셋 발생 알림 */
  resetDuringPlay?: boolean;
  isMock: boolean;
}

export interface SubmitEventChoiceRequest {
  clientRequestId: string;
  eventId: string;
  choice: string | number | null; // 옵션 id, pick index, 또는 null (즉시 이벤트는 비정상 호출)
}

export interface SubmitEventChoiceResponse {
  eventId: string;
  delta: number;
  scoreAfter: number;
  shieldConsumed?: boolean;
  /** ISSUE-05: 방패 발동 시 "원래 손실 금액" (양의 정수). 결과창 3단 연출용. */
  grossLoss?: number;
  outcomeLabel?: string;
  /**
   * 워프 (E9) 도착 효과 분류 — 서버가 target.kind 로 명시. 다른 이벤트는 undefined.
   * 'score' = 점수 보상 (E10 — delta=0 "꽝" 포함)
   * 'shield' = 방패 획득 (E8)
   * 'trap' = 무인도 진입 (TRAP)
   * 'start' = START 도착 (바퀴 보너스 미지급)
   * 'pending' = VAULT/SHOP/E1~E7 재 pending (ChooseStage 재마운트)
   * 'none' = 그 외
   */
  arrivalKind?: 'score' | 'shield' | 'trap' | 'start' | 'pending' | 'none';
  /** 방패 획득 후 보유량 (arrivalKind='shield' 전용) — shieldCount 표시 */
  shieldsOwnedAfter?: number;
  /** E7 추첨 숫자 (1~10). 다른 이벤트에서는 undefined. 멱등 캐시 포함 — 재시도 시 동일 숫자 반환. */
  drawnNumber?: number;
  /** QA Issue #3: E9 워프 destination cell kind. FE 체인 애니메이션 분기용. */
  arrivalCellKind?: 'E8' | 'E10' | 'safe';
  /** QA Issue #4: E9→E8 체인 방패 cap 초과 시 BE가 명시. */
  shieldsCapReached?: boolean;
  isMock: boolean;
}
